// ServiceList.js
import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { cqAPI } from "../../../lib/Auth";

// helper: build search string, conditionally adding note
// helper: build search string, conditionally adding note, plus ANY extra keys you pass
const buildSearch = (baseSearch, { addNote, note, ...rest } = {}) => {
  const params = new URLSearchParams(baseSearch);

  // Handle note (special rule)
  if (addNote && note?.trim()) {
    params.set("note", note.trim());
  } else {
    params.delete("note");
  }

  // Handle any other keys passed in (from, days, etc.)
  Object.entries(rest).forEach(([key, value]) => {
    // remove key if value is "empty-ish"
    if (
      value === undefined ||
      value === null ||
      (typeof value === "string" && !value.trim())
    ) {
      params.delete(key);
      return;
    }

    // If arrays are passed, append multiple values: ?k=a&k=b
    if (Array.isArray(value)) {
      params.delete(key);
      value.forEach((v) => {
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          params.append(key, String(v));
        }
      });
      return;
    }

    // Otherwise set single value
    params.set(key, String(value));
  });

  const s = params.toString();
  return s ? `?${s}` : "";
};



const ServiceList = ({ services, onSelectService }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingService, setPendingService] = useState(null); // service the user clicked
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const initialRef = useRef(null);

  if (!services?.length) return null;

  const handleServiceClick = (service) => {
    // If service id is "1", require the issue modal first
    if (String(service.id) === '1') {
      setPendingService(service);
      setDescription('');
      setTouched(false);
      onOpen();
      // focus textarea on open is handled by initialFocusRef
      return;
    }

    const search = buildSearch(location.search, {
      addNote: String(service.id).startsWith('1:'),
      note: service.name,
      ...service.from? {
        from : service.from
      } : {},
      ...service.days? {
        days : service.days
      } : {}
    });
    navigate({
      pathname: `/appointments/new/service/${service.id}`,
      search
    });
  };

  const closeModal = () => {
    setPendingService(null);
    setDescription('');
    setTouched(false);
    setSubmitting(false);
    onClose();
  };

  const submitIssueThenNavigate = async () => {
    setTouched(true);
    if (!description.trim()) return; // required

    setSubmitting(true);
    try {
      const res = await cqAPI.post('/services/new-issue', {
        description: description.trim(),
      });

      // If server indicates failure, show toast and allow resubmission
      if (res?.type !== 'ok') {
        toast({
          title: 'Could not submit your issue',
          description: res?.msg || 'Please try again.',
          status: 'error',
          isClosable: true,
        });
        setSubmitting(false); // keep modal open, let user edit/resubmit
        return;
      }
      const search = buildSearch(location.search, {
        addNote: pendingService && String(pendingService.id) === '1',
        note: description,
        ...pendingService.from? {
          from : pendingService.from
        } : {},
        ...pendingService.days? {
          days : pendingService.days
        } : {}
      });
      navigate({
        pathname: `/appointments/new/service/${res.data}`,
        search
      });

      closeModal();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Could not submit your issue',
        description:
          err?.message || 'Please try again or contact support if it persists.',
        status: 'error',
        isClosable: true,
      });
      setSubmitting(false);
    }
  };
  

  const isInvalid = touched && !description.trim();

  return (
    <Box className="service-list" id="service-list">
      <List spacing={3}>
        {services.map((service, index) => (
          <Box
            className="actionableItem"
            key={index}
            onClick={() => handleServiceClick(service)}
            cursor="pointer"
            _hover={{ bg: 'gray.100' }}
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            p={4}
            id={`${service.name}-button`}
            role="button"
            aria-label={`Choose ${service.name}`}
          >
            {service.name}
          </Box>
        ))}
      </List>

      {/* Mandatory Issue Modal */}
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        isCentered
        initialFocusRef={initialRef}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Tell us a bit more</ModalHeader>
          <ModalCloseButton isDisabled={submitting} />
          <ModalBody>
            <FormControl isRequired isInvalid={isInvalid}>
              <FormLabel>Please explain your issue</FormLabel>
              <Textarea
                ref={initialRef}
                placeholder="Describe what's going on..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => setTouched(true)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    // allow Cmd/Ctrl+Enter to submit
                    e.preventDefault();
                    submitIssueThenNavigate();
                  }
                }}
                disabled={submitting}
              />
              <FormErrorMessage>Description is required.</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={closeModal} isDisabled={submitting}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={submitIssueThenNavigate}
              isLoading={submitting}
              loadingText="Submitting"
            >
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ServiceList;
